import { IOptionsWithTypes, IOSMResponse, ITransportPassengersList, SectionId } from "@eventr/common";
import { ColoursObj, Icon, TEventrIcons } from "@eventr/common-storybook";
import { Stack } from "@mui/material";
import { cardTranslation } from "i18n/translateHelpers";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { routes } from "routing";
import useDerivedScreenType from "v2/hooks/useDerivedScreenType";
import { useFormatValue } from "../../hooks/useFormatValue";
import { useMatchTagsAndUsers } from "../hooks/useMatchTagsAndUsers";
import { useObjectContext } from "../providers/objectProvider";
import { CardText } from "../subComponents/cardText";
import { CategoryList } from "../subComponents/categoryList";
import { ChatButton } from "../subComponents/chatButton";
import { EditButton } from "../subComponents/editButton";
import { ExpandableSection } from "../subComponents/expendableSection";
import { RowInfo } from "../subComponents/rowInfo";
import { RowInfoAddress } from "../subComponents/rowInfoAddress";
import { RowInfoNotes } from "../subComponents/rowInfoNotes";
import { TransportAttachments } from "./transportAttachment";
import { TransportDriver } from "./transportDriver";
import { TransportPassengers } from "./transportPassengers";

interface ITransportInfoProps {
  booking_ref?: string;
  pick_up: string;
  pick_up_location?: string;
  pick_up_address?: IOSMResponse;
  drop_off: string;
  drop_off_location?: string;
  drop_off_address?: IOSMResponse;
  documents?: string;
  additional_info?: string;
  passengers_list?: ITransportPassengersList[];
  chatNames?: string[];
  variant?: "expandable" | "static";
  categories?: string[];
  /** Leaves the guest's chat to the page, which carries it elsewhere. */
  joinOnly?: boolean;
}

// cars are already filtered at this point

export const TransportInfo = ({
  booking_ref,
  pick_up,
  pick_up_location,
  pick_up_address,
  drop_off,
  drop_off_location,
  drop_off_address,
  documents,
  additional_info,
  passengers_list,
  chatNames,
  variant = "expandable",
  categories,
  joinOnly,
}: ITransportInfoProps) => {
  const screenType = useDerivedScreenType();
  const { _id, past } = useObjectContext();
  const { itemId: idFromParams = "" } = useParams();
  const { formatValue } = useFormatValue();
  const { matchTagsAndUser } = useMatchTagsAndUsers();

  const passenger = useMemo(() => {
    if ((passengers_list?.length ?? 0) === 0) return null;

    return passengers_list?.find((p) => {
      return p?.users?.some((u) => matchTagsAndUser(u)) || p?.drivers?.some((u) => matchTagsAndUser(u));
    });
  }, [passengers_list, matchTagsAndUser]);

  const allUsers = useMemo(() => {
    const all: IOptionsWithTypes[] = [];
    passengers_list?.forEach((p) => {
      all.push(...(p?.users ?? []));
      all.push(...(p?.drivers ?? []));
    });
    return all;
  }, [passengers_list]);

  const allDrivers = useMemo(() => {
    const all: IOptionsWithTypes[] = [];
    passengers_list?.forEach((p) => {
      all.push(...(p?.drivers ?? []));
    });
    return all;
  }, [passengers_list]);

  const allPassengers = useMemo(() => {
    const all: IOptionsWithTypes[] = [];
    passengers_list?.forEach((p) => {
      all.push(...(p?.users ?? []));
    });
    return all;
  }, [passengers_list]);

  const edit = useMemo(() => {
    if (screenType === "edit") {
      return <EditButton path={routes.edit.transportItem.build({ itemId: _id || idFromParams })} />;
    }

    return (
      <ChatButton
        _id={_id || idFromParams}
        users={allUsers}
        names={chatNames ?? []}
        sectionId={SectionId.TRANSPORT}
        joinOnly={joinOnly}
      />
    );
  }, [screenType, _id, idFromParams, allUsers, chatNames, joinOnly]);

  const isTransportPIT = formatValue(pick_up, "datetime") === formatValue(drop_off, "datetime");

  const headerMid = <CardText variant="body1">{booking_ref}</CardText>;

  const content = (
    <Stack spacing="8px">
      <CardText variant="bodyBold">{cardTranslation("Pick-up & Drop-off")}</CardText>

      {isTransportPIT ? (
        <TransportTimeRow
          location={pick_up_location}
          address={pick_up_address}
          value={formatValue(pick_up, "datetime")}
          icon="Right"
          past={past ?? false}
          text={cardTranslation("Transport_PIT")}
        />
      ) : (
        <>
          {pick_up && (
            <TransportTimeRow
              location={pick_up_location}
              address={pick_up_address}
              value={formatValue(pick_up, "datetime")}
              icon="Right"
              past={past ?? false}
              text={cardTranslation("Pick-up")}
            />
          )}

          {drop_off && (
            <TransportTimeRow
              location={drop_off_location}
              address={drop_off_address}
              value={formatValue(drop_off, "datetime")}
              icon="Left"
              past={past ?? false}
              text={cardTranslation("Drop-off")}
            />
          )}
        </>
      )}

      {(documents || additional_info) && <CardText variant="bodyBold">{cardTranslation("Attachments")}</CardText>}

      {additional_info && <RowInfoNotes notes={additional_info} />}
      {documents && <TransportAttachments extra_docs={documents} />}

      {(screenType === "personal" || screenType === "upNext") &&
        passenger &&
        (passenger.notes || passenger.documents) && (
          <CardText variant="bodyBold">{cardTranslation("Personal Information")}</CardText>
        )}

      {(screenType === "personal" || screenType === "upNext") && passenger?.notes && (
        <RowInfoNotes notes={passenger.notes} />
      )}

      {(screenType === "personal" || screenType === "upNext") && passenger?.documents && (
        <TransportAttachments extra_docs={passenger.documents} />
      )}
      <CategoryList categories={categories} />
      {allDrivers.length > 0 && <TransportDriver driver={allDrivers} />}

      {allPassengers.length > 0 && <TransportPassengers passengers={allPassengers} />}
    </Stack>
  );

  if (variant === "static") {
    return (
      <Stack
        direction="column"
        sx={{
          gap: "8px",
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {headerMid}
          {edit}
        </Stack>

        {content}
      </Stack>
    );
  }

  return (
    <Stack
      direction="column"
      sx={{
        gap: "8px",
      }}
    >
      <ExpandableSection mid={headerMid} end={edit}>
        {content}
      </ExpandableSection>
    </Stack>
  );
};

const TransportTimeRow = ({
  location,
  address,
  value,
  icon,
  past,
  text,
}: {
  location: string | undefined;
  address: IOSMResponse | undefined;
  value: any;
  icon: TEventrIcons;
  text?: string;
  past: boolean;
}) => {
  return (
    <Stack
      sx={{
        gap: "8px",
      }}
    >
      <RowInfo
        start={<Icon name={icon} bubble bubbleColour={past ? ColoursObj.shades.shade2 : ColoursObj.core.secondary} />}
        middle={<CardText>{text}</CardText>}
        end={<CardText>{value}</CardText>}
      />
      {location && (
        <RowInfo
          start={<Icon bubble bubbleColour={ColoursObj.coreBrights.secondary} name="Location" />}
          middle={<CardText useMarkdown>{location}</CardText>}
        />
      )}
      {address && <RowInfoAddress address={address} />}
    </Stack>
  );
};
